import React, { useMemo, useState, useContext, useRef } from "react";
import { AssetContext } from "../contexts/AssetListContext.js";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";
import "../css/Graphics.css";
import Config from "../config";
import "../css/WorkspaceTabs.css";
import WorkspaceYearSelect from "./WorkspaceYearSelect.js";
import {
    riskRatingLabels,
    riskScoreLabels,
    riskCategoryColors,
    riskColorMap,
    riskRatings,
    severityLabels,
    severityScoreLabels,
    severityLevels,
} from "../utils/riskConfig";

const Graphics = () => {
    const {
        defects,
        selectedYear,
        setSelectedYear,
        availableYears = [],
        isDefectsLoading = false,
    } = useContext(AssetContext);
    const [viewMode, setViewMode] = useState("risk-rating"); // 'risk-rating' or 'severity-level'
    const [isExporting, setIsExporting] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef(null);

    // Filter states for horizontal bar charts (one boolean per risk level)
    const initFilters = () => riskRatings.map(() => true);
    const [assetFilters, setAssetFilters] = useState(initFilters);
    const [defectTypeFilters, setDefectTypeFilters] = useState(initFilters);

    // Custom tooltip state
    const [tooltip, setTooltip] = useState({
        show: false,
        content: "",
        x: 0,
        y: 0,
    });

    // Tooltip handlers
    const showTooltip = (e, content) => {
        const rect = e.target.getBoundingClientRect();
        setTooltip({
            show: true,
            content,
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
        });
    };

    const hideTooltip = () => {
        setTooltip((prev) => ({ ...prev, show: false }));
    };

    // Configuration for each view mode (labels & sublabels from config.json)
    const viewConfig = {
        "risk-rating": {
            labels: riskRatingLabels,
            sublabels: riskScoreLabels,
            field: "risk_rate",
            donutTitle: "Defects by Risk Rating",
            barTitle: "Defects by Risk Rating and Task Type",
            assetTitle: "Defects by Asset and Risk Rating",
            defectTypeTitle: "Defects by Defect Type and Risk Rating",
            statusTitle: "Open vs Completed Defects by Risk Rating",
            printLabel: "Risk Rating",
        },
        "severity-level": {
            labels: severityLabels,
            sublabels: severityScoreLabels,
            field: "severity_level",
            donutTitle: "Defects by Severity Level",
            barTitle: "Defects by Severity Level and Task Type",
            assetTitle: "Defects by Asset and Severity Level",
            defectTypeTitle: "Defects by Defect Type and Severity Level",
            statusTitle: "Open vs Completed Defects by Severity Level",
            printLabel: "Severity Level",
        },
    };

    const currentConfig = viewConfig[viewMode];

    const categoryColors = useMemo(() => {
        // Severity has no colors in config.json; set a UI palette to match the design.
        if (viewMode === "severity-level") {
            return ["#EE3300", "#F28C28", "#FCCE06", "#77CC44"];
        }
        return riskCategoryColors;
    }, [viewMode]);

    const scopedCssVars = useMemo(() => {
        const hexToRgba = (hex, alpha) => {
            const h = String(hex || "").replace("#", "");
            if (h.length !== 6) return `rgba(0, 0, 0, ${alpha})`;
            const r = parseInt(h.slice(0, 2), 16);
            const g = parseInt(h.slice(2, 4), 16);
            const b = parseInt(h.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        // cat-1..4 map to --risk-color-4..1 throughout Graphics.css.
        const c4 = categoryColors[0];
        const c3 = categoryColors[1];
        const c2 = categoryColors[2];
        const c1 = categoryColors[3];

        return {
            "--risk-color-4": c4,
            "--risk-color-3": c3,
            "--risk-color-2": c2,
            "--risk-color-1": c1,
            "--risk-color-4-light": hexToRgba(c4, 0.08),
            "--risk-color-3-light": hexToRgba(c3, 0.08),
            "--risk-color-2-light": hexToRgba(c2, 0.08),
            "--risk-color-1-light": hexToRgba(c1, 0.08),
        };
    }, [categoryColors]);

    const getReadableBadgeTextColor = (hexColor) => {
        const h = String(hexColor || "").replace("#", "");
        if (h.length !== 6) return "#FFFFFF";
        const r = parseInt(h.slice(0, 2), 16) / 255;
        const g = parseInt(h.slice(2, 4), 16) / 255;
        const b = parseInt(h.slice(4, 6), 16) / 255;
        const lin = (v) =>
            v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
        const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
        return L > 0.55 ? "#333333" : "#FFFFFF";
    };

    // Reset filters when switching view mode
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        setAssetFilters(initFilters());
        setDefectTypeFilters(initFilters());
    };

    // Calculate KPI counts
    const getKpiCounts = () => {
        const counts = currentConfig.labels.map(
            (label) =>
                defects.filter((d) => d[currentConfig.field] === label).length,
        );
        return counts;
    };

    // Calculate donut chart data
    const getDonutData = () => {
        return currentConfig.labels.map((label, idx) => ({
            name: label,
            value: defects.filter((d) => d[currentConfig.field] === label)
                .length,
            color: categoryColors[idx],
        }));
    };

    // Calculate bar chart data (by Task Type)
    const getTaskTypeData = () => {
        return currentConfig.labels.map((label, idx) => {
            const filtered = defects.filter(
                (d) => d[currentConfig.field] === label,
            );
            return {
                name: label,
                Corrective: filtered.filter(
                    (d) => d["Task type"] === "Corrective",
                ).length,
                Preventive: filtered.filter(
                    (d) => d["Task type"] === "Preventive",
                ).length,
                color: categoryColors[idx],
            };
        });
    };

    // Calculate horizontal bar data by asset
    const getAssetData = () => {
        const assetGroups = {};
        defects.forEach((defect) => {
            const assetName = defect.asset_name;
            const category = defect[currentConfig.field];
            const categoryIdx = currentConfig.labels.indexOf(category);

            if (!assetGroups[assetName]) {
                assetGroups[assetName] = [0, 0, 0, 0];
            }
            if (categoryIdx !== -1) {
                assetGroups[assetName][categoryIdx]++;
            }
        });

        return Object.entries(assetGroups)
            .map(([name, values]) => ({ name, values }))
            .sort((a, b) => {
                const totalA = a.values.reduce(
                    (sum, v, idx) => sum + (assetFilters[idx] ? v : 0),
                    0,
                );
                const totalB = b.values.reduce(
                    (sum, v, idx) => sum + (assetFilters[idx] ? v : 0),
                    0,
                );
                return totalB - totalA;
            });
    };

    // Calculate horizontal bar data by defect type
    const getDefectTypeData = () => {
        const defectTypeGroups = {};
        defects.forEach((defect) => {
            const defectType = defect.defect_type;
            const category = defect[currentConfig.field];
            const categoryIdx = currentConfig.labels.indexOf(category);

            if (!defectTypeGroups[defectType]) {
                defectTypeGroups[defectType] = [0, 0, 0, 0];
            }
            if (categoryIdx !== -1) {
                defectTypeGroups[defectType][categoryIdx]++;
            }
        });

        return Object.entries(defectTypeGroups)
            .map(([name, values]) => ({ name, values }))
            .sort((a, b) => {
                const totalA = a.values.reduce(
                    (sum, v, idx) => sum + (defectTypeFilters[idx] ? v : 0),
                    0,
                );
                const totalB = b.values.reduce(
                    (sum, v, idx) => sum + (defectTypeFilters[idx] ? v : 0),
                    0,
                );
                return totalB - totalA;
            });
    };

    // Calculate open vs completed data
    const getStatusData = () => {
        const openData = currentConfig.labels.map(
            (label) =>
                defects.filter(
                    (d) =>
                        d[currentConfig.field] === label &&
                        d["Job Status"] !== "Work Completed",
                ).length,
        );
        const completedData = currentConfig.labels.map(
            (label) =>
                defects.filter(
                    (d) =>
                        d[currentConfig.field] === label &&
                        d["Job Status"] === "Work Completed",
                ).length,
        );
        return { openData, completedData };
    };

    const kpiCounts = getKpiCounts();
    const donutData = getDonutData();
    const taskTypeData = getTaskTypeData();
    const assetData = getAssetData();
    const defectTypeData = getDefectTypeData();
    const statusData = getStatusData();
    const totalDefects = defects.length;

    // Calculate max value for horizontal bars
    const getMaxBarValue = (data, filters) => {
        const totals = data.map((item) =>
            item.values.reduce(
                (sum, v, idx) => sum + (filters[idx] ? v : 0),
                0,
            ),
        );
        return Math.max(...totals, 1);
    };

    // Render horizontal bar row
    const renderHorizontalBar = (item, filters, maxValue) => {
        const filteredTotal = item.values.reduce(
            (sum, v, idx) => sum + (filters[idx] ? v : 0),
            0,
        );

        return (
            <div className="hbar-row" key={item.name}>
                <div className="hbar-label" title={item.name}>
                    {item.name}
                </div>
                <div className="hbar-track">
                    {item.values.map((val, idx) => {
                        if (val === 0 || !filters[idx]) return null;
                        const width = (val / maxValue) * 100;
                        return (
                            <div
                                key={idx}
                                className={`hbar-segment cat-${idx + 1}`}
                                style={{
                                    width: `${width}%`,
                                    cursor: "pointer",
                                }}
                                onMouseEnter={(e) =>
                                    showTooltip(
                                        e,
                                        `${currentConfig.labels[idx]}: ${val}`,
                                    )
                                }
                                onMouseLeave={hideTooltip}
                            >
                                {val > 0 && val}
                            </div>
                        );
                    })}
                </div>
                <div className="hbar-total">{filteredTotal}</div>
            </div>
        );
    };

    // Render status bars
    const renderStatusBars = (data, labels) => {
        const maxValue = Math.max(...data, 1);
        return labels.map((label, idx) => (
            <div className="status-bar-row" key={label}>
                <span className="status-bar-label">{label}</span>
                <div
                    className="status-bar-track"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) =>
                        showTooltip(e, `${label}: ${data[idx]}`)
                    }
                    onMouseLeave={hideTooltip}
                >
                    <div
                        className={`status-bar-fill cat-${idx + 1}`}
                        style={{ width: `${(data[idx] / maxValue) * 100}%` }}
                    />
                </div>
                <span className="status-bar-value">{data[idx]}</span>
            </div>
        ));
    };

    // Custom tooltip for bar chart
    const CustomBarTooltip = ({ active, payload, label }) => {
        if (!active || !payload) return null;
        return (
            <div className="custom-tooltip">
                <p className="tooltip-label">{label}</p>
                {payload.map((item, idx) => (
                    <p key={idx} style={{ color: item.color }}>
                        {item.name}: {item.value}
                    </p>
                ))}
            </div>
        );
    };

    // Close the export dropdown when clicking outside it.
    React.useEffect(() => {
        const onClickOutside = (e) => {
            if (
                exportMenuRef.current &&
                !exportMenuRef.current.contains(e.target)
            ) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const handleExport = async (format) => {
        setExportMenuOpen(false);
        if (isExporting) return;
        setIsExporting(true);
        try {
            const filename =
                Config.defectFiles?.[selectedYear] ?? "defect_list.xlsx";
            const url = `/api/export?format=${format}&filename=${encodeURIComponent(filename)}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Export failed (${res.status})`);
            }
            const blob = await res.blob();
            const ext = format === "pdf" ? "pdf" : "zip";
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = `defect-report${selectedYear ? `-${selectedYear}` : ""}.${ext}`;
            a.click();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="defect-summary-container" style={scopedCssVars}>
            {/* Navigation Bar */}
            <div className="cost-analytics-title workspace-page-toolbar">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                    }}
                >
                    <WorkspaceYearSelect
                        id="graphics-year-select"
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        availableYears={availableYears}
                        disabled={
                            availableYears.length === 0 || isDefectsLoading
                        }
                    />

                    {/* Toggle Switch */}
                    <div className="view-toggle-container">
                        <span
                            className="view-toggle-label"
                            data-current-view={currentConfig.printLabel}
                        >
                            View by:
                        </span>
                        <div className="view-toggle-switch">
                            <button
                                className={`view-toggle-option ${viewMode === "risk-rating" ? "active" : ""}`}
                                onClick={() =>
                                    handleViewModeChange("risk-rating")
                                }
                            >
                                Risk Rating
                            </button>
                            {severityLevels.length > 0 && (
                                <button
                                    className={`view-toggle-option ${viewMode === "severity-level" ? "active" : ""}`}
                                    onClick={() =>
                                        handleViewModeChange("severity-level")
                                    }
                                >
                                    Severity Level
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Legend
                    <div className="summary-legend">
                        {currentConfig.labels.map((label, idx) => (
                            <div className="summary-legend-item" key={label}>
                                <div
                                    className={`summary-legend-dot cat-${idx + 1}`}
                                ></div>
                                <span>{label}</span>
                            </div>
                        ))}
                    </div> */}

                    <div
                        className="export-dropdown"
                        ref={exportMenuRef}
                    >
                        <button
                            className="download-excel-button"
                            onClick={() =>
                                setExportMenuOpen((o) => !o)
                            }
                            disabled={isExporting}
                        >
                            {isExporting ? "Exporting…" : "📄 Export ▾"}
                        </button>
                        {exportMenuOpen && (
                            <div className="export-dropdown-menu">
                                <button
                                    onClick={() => handleExport("pdf")}
                                >
                                    PDF – one chart per page
                                </button>
                                <button
                                    onClick={() => handleExport("zip")}
                                >
                                    ZIP – individual images
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="summary-main-content">
                {isDefectsLoading ? (
                    <div
                        className="summary-card full-width"
                        style={{
                            gridColumn: "1 / -1",
                            textAlign: "center",
                            padding: "48px 24px",
                        }}
                    >
                        Loading defect data…
                    </div>
                ) : (
                    <>
                        {/* KPI Cards Row */}
                        <div className="kpi-row">
                            {currentConfig.labels.map((label, idx) => (
                                <div
                                    className={`kpi-card cat-${idx + 1}`}
                                    key={label}
                                >
                                    <div
                                        className="kpi-badge"
                                        style={{
                                            backgroundColor:
                                                categoryColors[idx],
                                            color: getReadableBadgeTextColor(
                                                categoryColors[idx],
                                            ),
                                        }}
                                    >
                                        {label}
                                    </div>
                                    <div className="kpi-value">
                                        {kpiCounts[idx]}
                                    </div>
                                    <div className="kpi-sublabel">
                                        {currentConfig.sublabels[idx]}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Donut Chart */}
                        <div className="summary-card">
                            <h3 className="summary-card-title">
                                {currentConfig.donutTitle}
                            </h3>
                            <div className="donut-container">
                                <div className="donut-chart-wrapper">
                                    <ResponsiveContainer
                                        width="100%"
                                        height={260}
                                    >
                                        <PieChart>
                                            <Pie
                                                data={donutData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={70}
                                                outerRadius={110}
                                                dataKey="value"
                                            >
                                                {donutData.map((entry, idx) => (
                                                    <Cell
                                                        key={idx}
                                                        fill={
                                                            categoryColors[idx]
                                                        }
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [
                                                    value,
                                                    name,
                                                ]}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="donut-center-text">
                                        <div className="donut-total">
                                            {totalDefects}
                                        </div>
                                        <div className="donut-label">
                                            Total Defects
                                        </div>
                                    </div>
                                </div>
                                <div className="chart-legend">
                                    {donutData.map((item, idx) => (
                                        <div
                                            className="chart-legend-item"
                                            key={item.name}
                                        >
                                            <div
                                                className="chart-legend-box"
                                                style={{
                                                    background:
                                                        categoryColors[idx],
                                                }}
                                            ></div>
                                            <span>
                                                {item.name} ({item.value})
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Bar Chart - Task Type - Using Blue and Grey colors */}
                        <div className="summary-card">
                            <h3 className="summary-card-title">
                                {currentConfig.barTitle}
                            </h3>
                            <div className="bar-chart-container">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={taskTypeData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tick={{
                                                fontSize: 12,
                                                fontFamily: "Arboria Book",
                                            }}
                                        />
                                        <YAxis
                                            tick={{
                                                fontSize: 12,
                                                fontFamily: "Arboria Book",
                                            }}
                                        />
                                        <Tooltip
                                            content={<CustomBarTooltip />}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontFamily: "Arboria Book",
                                                fontSize: 12,
                                            }}
                                        />
                                        <Bar
                                            dataKey="Corrective"
                                            name="Corrective (C)"
                                            fill="#5B9BD5"
                                        />
                                        <Bar
                                            dataKey="Preventive"
                                            name="Preventive (P)"
                                            fill="#A6A6A6"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="chart-filter-row">
                                <span
                                    style={{
                                        fontSize: "12px",
                                        color: "#767676",
                                        fontFamily: "Arboria Book",
                                    }}
                                >
                                    C = Corrective &nbsp; | &nbsp; P =
                                    Preventive
                                </span>
                            </div>
                        </div>

                        {/* Horizontal Bar - By Asset */}
                        <div className="summary-card">
                            <h3 className="summary-card-title">
                                {currentConfig.assetTitle}
                            </h3>
                            <div className="hbar-container">
                                {assetData.map((item) =>
                                    renderHorizontalBar(
                                        item,
                                        assetFilters,
                                        getMaxBarValue(assetData, assetFilters),
                                    ),
                                )}
                            </div>
                            <div className="chart-filter-row">
                                {currentConfig.labels.map((label, idx) => (
                                    <label
                                        className="chart-filter-item"
                                        key={label}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={assetFilters[idx]}
                                            onChange={() => {
                                                const newFilters = [
                                                    ...assetFilters,
                                                ];
                                                newFilters[idx] =
                                                    !newFilters[idx];
                                                setAssetFilters(newFilters);
                                            }}
                                        />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Horizontal Bar - By Defect Type */}
                        <div className="summary-card">
                            <h3 className="summary-card-title">
                                {currentConfig.defectTypeTitle}
                            </h3>
                            <div className="hbar-container">
                                {defectTypeData.map((item) =>
                                    renderHorizontalBar(
                                        item,
                                        defectTypeFilters,
                                        getMaxBarValue(
                                            defectTypeData,
                                            defectTypeFilters,
                                        ),
                                    ),
                                )}
                            </div>
                            <div className="chart-filter-row">
                                {currentConfig.labels.map((label, idx) => (
                                    <label
                                        className="chart-filter-item"
                                        key={label}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={defectTypeFilters[idx]}
                                            onChange={() => {
                                                const newFilters = [
                                                    ...defectTypeFilters,
                                                ];
                                                newFilters[idx] =
                                                    !newFilters[idx];
                                                setDefectTypeFilters(
                                                    newFilters,
                                                );
                                            }}
                                        />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Open vs Completed */}
                        <div className="summary-card full-width">
                            <h3 className="summary-card-title">
                                {currentConfig.statusTitle}
                            </h3>
                            <div className="status-grid">
                                <div className="status-section">
                                    <div className="status-header">
                                        <span className="status-title">
                                            Open Defects
                                        </span>
                                        <span className="status-badge open">
                                            {statusData.openData.reduce(
                                                (a, b) => a + b,
                                                0,
                                            )}{" "}
                                            Total
                                        </span>
                                    </div>
                                    <div className="status-bars">
                                        {renderStatusBars(
                                            statusData.openData,
                                            currentConfig.labels,
                                        )}
                                    </div>
                                </div>
                                <div className="status-section">
                                    <div className="status-header">
                                        <span className="status-title">
                                            Completed Defects
                                        </span>
                                        <span className="status-badge completed">
                                            {statusData.completedData.reduce(
                                                (a, b) => a + b,
                                                0,
                                            )}{" "}
                                            Total
                                        </span>
                                    </div>
                                    <div className="status-bars">
                                        {renderStatusBars(
                                            statusData.completedData,
                                            currentConfig.labels,
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reference Boxes */}
                        <div className="summary-card full-width risk-dashboard-reference-card">
                            <div className="reference-container">
                                {/* Risk Rating Reference */}
                                <div className="reference-box">
                                    <div className="reference-title">
                                        Risk Rating Reference (GCAA Risk Matrix)
                                    </div>
                                    <div className="reference-scale">
                                        {riskRatings.map((r, idx) => (
                                            <div
                                                className={`reference-item cat-${idx + 1}`}
                                                key={r.label}
                                            >
                                                <div className="color-bar"></div>
                                                <div className="content">
                                                    <div className="reference-item-title">
                                                        {r.label}
                                                    </div>
                                                    <div className="reference-item-desc">
                                                        {r.description}
                                                    </div>
                                                    <div className="reference-item-score">
                                                        Risk Score:{" "}
                                                        {r.scoreRange}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Severity Level Reference */}
                                {severityLevels.length > 0 && (
                                    <div className="reference-box">
                                        <div className="reference-title">
                                            Severity Level Reference (Fixed
                                            Structures Guideline)
                                        </div>
                                        <div className="reference-scale">
                                            {severityLevels.map((s, idx) => (
                                                <div
                                                    className={`reference-item cat-${idx + 1}`}
                                                    key={s.label}
                                                >
                                                    <div className="color-bar"></div>
                                                    <div className="content">
                                                        <div className="reference-item-title">
                                                            {s.label}
                                                        </div>
                                                        <div className="reference-item-desc">
                                                            {s.description}
                                                        </div>
                                                        <div className="reference-item-score">
                                                            Risk Score:{" "}
                                                            {s.scoreRange}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>

            {/* Custom Styled Tooltip */}
            {tooltip.show && (
                <div
                    className="styled-tooltip"
                    style={{
                        position: "fixed",
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: "translate(-50%, -100%)",
                    }}
                >
                    {tooltip.content}
                </div>
            )}
        </div>
    );
};

export default Graphics;
